import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import suggestion from './suggestion';
import './TiptapEditor.css';

const MenuBar = ({ editor }) => {
	if (!editor) return null;

	return (
		<div className='editor-toolbar'>
			<div className='toolbar-group'>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleBold().run()}
					className={editor.isActive('bold') ? 'is-active' : ''}>
					<i className='fa-solid fa-bold'></i>
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleItalic().run()}
					className={editor.isActive('italic') ? 'is-active' : ''}>
					<i className='fa-solid fa-italic'></i>
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleStrike().run()}
					className={editor.isActive('strike') ? 'is-active' : ''}>
					<i className='fa-solid fa-strikethrough'></i>
				</button>
			</div>

			<div className='toolbar-divider'></div>

			<div className='toolbar-group'>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
					className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}>
					H1
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
					className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}>
					H2
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
					className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}>
					H3
				</button>
			</div>

			<div className='toolbar-divider'></div>

			<div className='toolbar-group'>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					className={editor.isActive('bulletList') ? 'is-active' : ''}>
					<i className='fa-solid fa-list-ul'></i>
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					className={editor.isActive('orderedList') ? 'is-active' : ''}>
					<i className='fa-solid fa-list-ol'></i>
				</button>
			</div>

			<div className='toolbar-divider'></div>

			<div className='toolbar-group'>
				<button
					type='button'
					onClick={() => editor.chain().focus().toggleBlockquote().run()}
					className={editor.isActive('blockquote') ? 'is-active' : ''}>
					<i className='fa-solid fa-quote-left'></i>
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().setHorizontalRule().run()}>
					<i className='fa-solid fa-minus'></i>
				</button>
			</div>

			<div className='toolbar-divider'></div>

			<div className='toolbar-group'>
				<button
					type='button'
					onClick={() => editor.chain().focus().undo().run()}
					disabled={!editor.can().undo()}>
					<i className='fa-solid fa-rotate-left'></i>
				</button>
				<button
					type='button'
					onClick={() => editor.chain().focus().redo().run()}
					disabled={!editor.can().redo()}>
					<i className='fa-solid fa-rotate-right'></i>
				</button>
			</div>

			<div className='toolbar-divider'></div>

			<div className='toolbar-group'>
				<span className='toolbar-hint'>
					<i className='fa-solid fa-at'></i>
					mention student
				</span>
			</div>
		</div>
	);
};

const TiptapEditor = ({ content, onUpdate, placeholder = 'Start writing...', students = [] }) => {
	const studentsRef = useRef(students);

	// Keep students ref updated
	useEffect(() => {
		studentsRef.current = students;
	}, [students]);

	const editor = useEditor({
		extensions: [
			StarterKit,
			Placeholder.configure({
				placeholder,
			}),
			Mention.configure({
				HTMLAttributes: {
					class: 'mention',
				},
				suggestion: suggestion(() => studentsRef.current),
			}),
		],
		content: content || '',
		onUpdate: ({ editor }) => {
			if (onUpdate) {
				// Extract mentioned student IDs
				const json = editor.getJSON();
				const mentionedStudentIds = [];
				const extractMentions = (node) => {
					if (node.type === 'mention' && node.attrs?.id) {
						mentionedStudentIds.push(node.attrs.id);
					}
					if (node.content) {
						node.content.forEach(extractMentions);
					}
				};
				if (json.content) {
					json.content.forEach(extractMentions);
				}

				onUpdate({
					json,
					html: editor.getHTML(),
					text: editor.getText(),
					mentionedStudentIds: [...new Set(mentionedStudentIds)],
				});
			}
		},
	});

	return (
		<div className='tiptap-editor'>
			<MenuBar editor={editor} />
			<EditorContent editor={editor} className='editor-content' />
		</div>
	);
};

export default TiptapEditor;
